// src/app/learn/[slug]/LessonClient.tsx
//
// v2 — adds KaTeX math rendering, inline SVG diagrams (referenced via
// [[diagram:<id>]] markers), flashcards sidebar with downloads, header
// meta chips (read time, difficulty, drill count), prerequisite + next
// links, interactive worked examples (try-then-reveal), and a
// printable summary PDF.

'use client'

import { stripMarkdown } from '@/lib/render/strip-markdown'
import PublishOnScreenMaterial from '@/components/tutor/PublishOnScreenMaterial'
import { askAboutExample } from '@/lib/tutor/on-screen-material'
import { useEffect, useState, type ReactNode } from 'react'
import { useSession } from 'next-auth/react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  Loader2,
  Clock,
  FileDown,
  Flag,
  GraduationCap,
  BookOpenCheck,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import Diagram from '@/components/lesson/Diagram'
import Flashcards from '@/components/lesson/Flashcards'
import MiniCheck, { type MiniCheckQuestion } from '@/components/lesson/MiniCheck'
import ConceptMap from '@/components/lesson/ConceptMap'
import FurtherReading, { type FurtherReadingPost } from '@/components/lesson/FurtherReading'
import VideoSection from '@/components/lesson/VideoSection'
import WorkedExampleCard from '@/components/lesson/WorkedExampleCard'
import PathBar from '@/components/lesson/PathBar'
import CheckpointCta from '@/components/lesson/CheckpointCta'
import VerifiedBadge from '@/components/lesson/VerifiedBadge'
import { useLessonComplete } from '@/components/lesson/useLessonComplete'
import { trackPathEvent } from '@/lib/analytics/track-path-event'
import {
  CHECKPOINT_COUNT,
  DEFAULT_PATH_GATE,
  conceptMapLinks,
  findStepBySlug,
} from '@/lib/learn/learning-path'
import type { PathProgress, PathStepProgress } from '@/lib/learn/path-progress'
import type {
  Flashcard,
  LessonDiagram,
  LessonMiniCheck,
  TopicLink,
  WorkedExampleChoice,
} from '@/lib/study-plan/lesson-generation'
import type { TopicVideo } from '@/lib/content/topic-videos'
import SessionHandoff from '@/components/study-session/SessionHandoff'

// Code-split the heavy markdown/math stack (react-markdown + remark/rehype
// + KaTeX) out of the lesson route's first-paint JS. ssr stays enabled so
// the public, SEO-relevant lesson content + math still server-render. (#753)
const MarkdownRenderer = dynamic(() => import('@/components/lesson/MarkdownRenderer'), {
  loading: () => <span className="text-sm text-muted-foreground">Loading…</span>,
})

export type LessonDto = {
  topic: string
  slug: string
  section: string | null
  content: {
    hook: string
    explanation: string
    workedExamples: Array<{
      problem: string
      solution: string
      insight: string
      choices?: WorkedExampleChoice[]
      correctChoice?: 'A' | 'B' | 'C' | 'D'
    }>
    pitfalls: Array<{ name: string; explanation: string }>
    keyTakeaways: string[]
    flashcards?: Flashcard[]
    diagrams?: LessonDiagram[]
    prerequisites?: TopicLink[]
    nextTopics?: TopicLink[]
    estimatedReadMinutes?: number
    difficulty?: 'intro' | 'easy' | 'medium' | 'hard'
    miniCheck?: LessonMiniCheck
  }
  generatedAt: string
  version: number
  /** When a person passed the review checklist; the Expert-verified mark renders only when set. */
  reviewedAt: string | null
  /** Mini-check question pre-fetched server-side. Null if not configured. */
  miniCheckQuestion: MiniCheckQuestion | null
  /** True if the visitor is signed in — drives flashcard SR persistence. */
  isAuthenticated: boolean
  /** True if the visitor has already marked this lesson as read (server-
   *  fetched from LessonRead). The toggle component below uses this as
   *  its initial state. Anon users always see `false`. */
  initialRead: boolean
  /** Curated Khan Academy videos for this topic. Empty array if none. */
  videos: TopicVideo[]
  /** Curated blog articles for this topic (blog-learn-links.ts, server-
   *  resolved to posts that are actually published). Empty array if none. */
  furtherReading: FurtherReadingPost[]
}

const DIFFICULTY_LABEL: Record<string, string> = {
  intro: 'Intro',
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
}

const DIFFICULTY_COLOR: Record<string, string> = {
  intro: 'bg-slate-700/40 text-foreground',
  easy: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  hard: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
}

export default function LessonClient({ lesson }: { lesson: LessonDto }) {
  const [pdfBusy, setPdfBusy] = useState(false)

  // Where this lesson sits on the learning path — a step's own lesson, or a
  // sub-topic module of one. Pure lookup, so it renders in the static HTML.
  // A lesson with no step (none today; a future lesson without an alias)
  // still gets a skill check on its own topic — the session resolves aliases.
  const step = findStepBySlug(lesson.slug)
  // The concept map's Before / Next, from the path graph (conceptMapLinks) so
  // it agrees with the path bar and never calls a prerequisite "Next". The
  // lesson's own generated links are only for a lesson that is not on the path.
  const mapLinks = conceptMapLinks(lesson.slug) ?? {
    prerequisites: lesson.content.prerequisites ?? [],
    nextTopics: lesson.content.nextTopics ?? [],
  }
  const checkpointStep = step ?? {
    topic: lesson.topic,
    checkpoint: { count: CHECKPOINT_COUNT },
    gate: DEFAULT_PATH_GATE,
  }

  // The lesson page is now statically generated for SEO (no per-request session
  // read), so auth + read state are hydrated CLIENT-side here as progressive
  // enhancement: the lesson content renders instantly for crawlers/everyone,
  // and the "I've read this" state fills in once we know who's viewing.
  const { status } = useSession()
  const isAuthenticated = status === 'authenticated'

  // "I've read this" toggle. Starts from the (static) prop, then hydrates from
  // /api/learn/read once we know the viewer. Optimistic update — flip the state
  // immediately, then POST/DELETE in the background. Anon users see the button
  // but the API 401s and we surface a sign-in prompt rather than toggling.
  const [isRead, setIsRead] = useState(lesson.initialRead)
  const [readBusy, setReadBusy] = useState(false)
  const [readError, setReadError] = useState<string | null>(null)

  // Measurement (docs/learning-path-plan.md, PR 5): a view of this step, and
  // "read" once the student reaches the skill check after enough time on the page.
  const stepTopic = step?.topic ?? lesson.topic
  useEffect(() => {
    if (step) trackPathEvent('path_step_view', step.topic)
  }, [step])
  const endOfLessonRef = useLessonComplete({
    slug: lesson.slug,
    topic: stepTopic,
    estimatedReadMinutes: lesson.content.estimatedReadMinutes,
    enabled: !!step,
  })

  // This step's progress (Passed / In progress + tier), hydrated the same way:
  // the static page cannot know who is reading it.
  const [stepProgress, setStepProgress] = useState<PathStepProgress | null>(null)
  // Every step's progress, for the "Prove I know this" offers on the skill
  // check card (what a pass would open; a locked step's prerequisites).
  const [pathSteps, setPathSteps] = useState<PathStepProgress[]>([])
  const [progressLoading, setProgressLoading] = useState(false)
  // Bumped when the path changes under this page (a placement check scored in
  // the "Prove I know this" dialog), so the progress below is fetched again.
  const [pathVersion, setPathVersion] = useState(0)
  useEffect(() => {
    if (!isAuthenticated || !step) return
    let cancelled = false
    setProgressLoading(true)
    fetch('/api/learn/path', { cache: 'no-store' })
      .then((r) => (r.ok ? (r.json() as Promise<PathProgress>) : null))
      .then((d) => {
        if (cancelled || !d) return
        setStepProgress(d.steps.find((s) => s.topic === step.topic) ?? null)
        setPathSteps(d.steps)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setProgressLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, step, pathVersion])

  // Hydrate the read state for a signed-in viewer (the static HTML can't know it).
  useEffect(() => {
    if (!isAuthenticated) return
    let cancelled = false
    fetch(`/api/learn/read?slug=${encodeURIComponent(lesson.slug)}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && typeof d.read === 'boolean') setIsRead(d.read)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, lesson.slug])

  async function toggleRead() {
    if (!isAuthenticated) {
      setReadError('Sign in to track your progress.')
      return
    }
    setReadBusy(true)
    setReadError(null)
    const optimistic = !isRead
    setIsRead(optimistic)
    try {
      const res = await fetch('/api/learn/read', {
        method: optimistic ? 'POST' : 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({ lessonSlug: lesson.slug }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    } catch (err) {
      // Revert the optimistic toggle and surface a soft error.
      setIsRead(!optimistic)
      setReadError(err instanceof Error ? err.message : "Couldn't save. Try again?")
    } finally {
      setReadBusy(false)
    }
  }

  const diagramsById = new Map((lesson.content.diagrams ?? []).map((d) => [d.id, d]))

  async function downloadSummaryPdf() {
    setPdfBusy(true)
    try {
      const { jsPDF } = await import('jspdf')
      const doc = new jsPDF({ unit: 'pt', format: 'letter' })
      const margin = 50
      const pageWidth = 612
      const wrapWidth = pageWidth - 2 * margin
      let y = margin

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(18)
      doc.text(lesson.topic, margin, y)
      y += 12
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.setTextColor(120)
      doc.text(
        `${lesson.section === 'MATH' ? 'Math' : 'Reading & Writing'} · UnlimitedTests`,
        margin,
        y,
      )
      y += 18
      doc.setTextColor(0)

      doc.setFont('helvetica', 'italic')
      doc.setFontSize(10)
      const hookLines = doc.splitTextToSize(stripMd(lesson.content.hook), wrapWidth)
      doc.text(hookLines, margin, y)
      y += hookLines.length * 12 + 10
      doc.setFont('helvetica', 'normal')

      // Key takeaways
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.text('Key takeaways', margin, y)
      y += 14
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      for (const t of lesson.content.keyTakeaways) {
        if (y > 720) {
          doc.addPage()
          y = margin
        }
        const lines = doc.splitTextToSize(`•  ${stripMd(t)}`, wrapWidth - 12)
        doc.text(lines, margin + 12, y)
        y += lines.length * 11 + 4
      }
      y += 8

      // Pitfalls
      if (lesson.content.pitfalls.length > 0) {
        if (y > 680) {
          doc.addPage()
          y = margin
        }
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(11)
        doc.text('Common pitfalls', margin, y)
        y += 14
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(10)
        for (const p of lesson.content.pitfalls) {
          if (y > 720) {
            doc.addPage()
            y = margin
          }
          doc.setFont('helvetica', 'bold')
          doc.text(`–  ${stripMd(p.name)}`, margin + 4, y)
          y += 12
          doc.setFont('helvetica', 'normal')
          const lines = doc.splitTextToSize(stripMd(p.explanation), wrapWidth - 16)
          doc.text(lines, margin + 16, y)
          y += lines.length * 11 + 6
        }
      }

      doc.save(`${lesson.slug}-summary.pdf`)
    } finally {
      setPdfBusy(false)
    }
  }

  return (
    <main className="min-h-dvh bg-background text-foreground">
      <div className="mx-auto max-w-6xl px-4 py-8">
        {/* Sage reads this lesson's worked examples by number (material-context.ts). */}
        <PublishOnScreenMaterial lesson={{ slug: lesson.slug }} label={`${lesson.topic} lesson`} />
        <Link
          href="/learn"
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Learning path
        </Link>

        <div className="grid gap-6 lg:grid-cols-[1fr,300px]">
          {/* Main column */}
          <article>
            <PathBar slug={lesson.slug} progress={stepProgress} />
            <header className="mb-5">
              {lesson.section && (
                <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
                  {lesson.section === 'MATH' ? 'Math' : 'Reading & Writing'}
                </div>
              )}
              <h1 className="text-3xl font-bold text-foreground">{lesson.topic}</h1>

              {/* Meta chips */}
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                <VerifiedBadge reviewedAt={lesson.reviewedAt} />
                {lesson.content.estimatedReadMinutes && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card/60 px-2.5 py-1 text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {lesson.content.estimatedReadMinutes} min read
                  </span>
                )}
                {lesson.content.difficulty && (
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${DIFFICULTY_COLOR[lesson.content.difficulty]}`}
                  >
                    <GraduationCap className="h-3 w-3" />
                    {DIFFICULTY_LABEL[lesson.content.difficulty]}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand/10 px-2.5 py-1 text-brand-ink">
                  <Flag className="h-3 w-3" />
                  {checkpointStep.checkpoint.count}-question skill check
                </span>
              </div>

              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {lesson.content.hook}
              </p>
            </header>

            {/* Concept map: shows prerequisites + this lesson + next-up */}
            <ConceptMap
              current={{ topic: lesson.topic, slug: lesson.slug }}
              prerequisites={mapLinks.prerequisites}
              nextTopics={mapLinks.nextTopics}
            />

            {/* Our own video for this topic leads — "watch the two-minute version, then
                read". Third-party supplements keep their place at the bottom; the same
                component renders both, sorted ours-first and worded by what is in the list. */}
            {lesson.videos.some((v) => v.source === 'UnlimitedTests') && (
              <VideoSection topic={lesson.topic} videos={lesson.videos} />
            )}

            {/* Concept explanation with embedded diagrams */}
            <section className="mb-8">
              <MarkdownWithDiagrams
                content={lesson.content.explanation}
                diagramsById={diagramsById}
              />
            </section>

            {/* Mini-check — quick "did this click?" question */}
            {lesson.miniCheckQuestion && (
              <MiniCheck
                prompt={lesson.content.miniCheck?.prompt}
                question={lesson.miniCheckQuestion}
              />
            )}

            {/* Worked examples */}
            {lesson.content.workedExamples.length > 0 && (
              <section className="mb-8 space-y-4">
                <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
                  <Sparkles className="h-5 w-5 text-brand-ink" />
                  Worked examples
                </h2>
                {lesson.content.workedExamples.map((ex, i) => (
                  <WorkedExampleCard
                    key={i}
                    index={i}
                    ask={askAboutExample(lesson.slug, lesson.topic, i)}
                    example={ex}
                    renderMarkdown={(content) => (
                      <MarkdownWithDiagrams content={content} diagramsById={diagramsById} />
                    )}
                  />
                ))}
              </section>
            )}

            {/* Pitfalls */}
            {lesson.content.pitfalls.length > 0 && (
              <section className="mb-8 space-y-3">
                <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
                  <AlertTriangle className="h-5 w-5 text-amber-400" />
                  Common pitfalls
                </h2>
                {lesson.content.pitfalls.map((p, i) => (
                  <div
                    key={i}
                    className="rounded-md border border-amber-500/20 bg-amber-50 dark:bg-amber-950/15 p-3"
                  >
                    <div className="font-medium text-amber-800 dark:text-amber-200">{p.name}</div>
                    <div className="mt-1 prose dark:prose-invert max-w-none text-sm prose-strong:text-amber-800 dark:prose-strong:text-amber-100">
                      <MarkdownRenderer>{p.explanation}</MarkdownRenderer>
                    </div>
                  </div>
                ))}
              </section>
            )}

            {/* Key takeaways */}
            {lesson.content.keyTakeaways.length > 0 && (
              <section className="mb-8">
                <h2 className="mb-3 flex items-center gap-2 text-xl font-semibold text-foreground">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  Key takeaways
                </h2>
                <ul className="space-y-2">
                  {lesson.content.keyTakeaways.map((t, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 rounded-md border border-border/60 bg-card/40 p-3 text-sm text-foreground"
                    >
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
                      <span className="prose dark:prose-invert prose-sm max-w-none">
                        <MarkdownRenderer>{t}</MarkdownRenderer>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Watch & learn — third-party walkthroughs stay below the lesson body since
                they're complementary, not the primary teaching. When one of OUR videos is
                in the list the whole section has already rendered up top instead. */}
            {lesson.videos.length > 0 &&
              !lesson.videos.some((v) => v.source === 'UnlimitedTests') && (
                <VideoSection topic={lesson.topic} videos={lesson.videos} />
              )}

            {/* Curated blog articles on this topic (blog ↔ learn cluster). */}
            <FurtherReading posts={lesson.furtherReading} />

            {/* "I've read this" toggle. Sits between the lesson body and
                the action CTAs ("Try it yourself" / "Print summary") so
                the natural reading flow is: finish lesson → mark read →
                pick next action. Anonymous users see the button but
                clicking surfaces an inline sign-in prompt. */}
            <section className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card/40 p-4">
              <Button
                onClick={() => void toggleRead()}
                disabled={readBusy}
                variant={isRead ? 'default' : 'outline'}
                size="lg"
                className={isRead ? 'bg-emerald-500 text-slate-950 hover:bg-emerald-400' : ''}
                aria-pressed={isRead}
              >
                {readBusy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <BookOpenCheck className="mr-2 h-4 w-4" />
                )}
                {isRead ? 'Marked as read' : "I've read this"}
              </Button>
              {readError && (
                <span className="text-sm text-amber-700 dark:text-amber-300">
                  {readError}
                  {!isAuthenticated && (
                    <Link
                      href="/signin"
                      className="ml-2 underline hover:text-amber-600 dark:hover:text-amber-200"
                    >
                      Sign in
                    </Link>
                  )}
                </span>
              )}
              {!readError && (
                <span className="text-xs text-muted-foreground">
                  {isRead
                    ? "Shows a ✓ on this lesson's card on /learn."
                    : 'Tracks your progress across lessons.'}
                </span>
              )}
            </section>

            {/* The skill check — ten questions on this step's topic, scored into the
                mastery tier the step gates on. Print summary rides along. */}
            <div ref={endOfLessonRef} aria-hidden="true" />
            {/* Opened as a block of a time-boxed session: hand off to its
                questions (renders nothing outside a session). */}
            <SessionHandoff className="mb-4" outcome={{ status: 'done', lessonRead: true }} />
            <CheckpointCta
              step={checkpointStep}
              progress={stepProgress}
              pathSteps={pathSteps}
              loading={progressLoading}
              onStart={() => trackPathEvent('checkpoint_start', stepTopic)}
              onPathChanged={() => setPathVersion((v) => v + 1)}
              secondary={
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => void downloadSummaryPdf()}
                  disabled={pdfBusy}
                >
                  {pdfBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <FileDown className="mr-2 h-4 w-4" />
                      Print summary
                    </>
                  )}
                </Button>
              }
            />

            {/* Bottom concept map mirrors the top one for navigation —
                useful after reading the whole lesson and deciding what's next. */}
            {mapLinks.nextTopics.length > 0 && (
              <ConceptMap
                current={{ topic: lesson.topic, slug: lesson.slug }}
                prerequisites={mapLinks.prerequisites}
                nextTopics={mapLinks.nextTopics}
              />
            )}

            <PathBar slug={lesson.slug} variant="bottom" />

            <footer className="mt-6 border-t border-border/60 pt-4 text-xs text-muted-foreground">
              <span>
                Lesson v{lesson.version} · generated{' '}
                {new Date(lesson.generatedAt).toLocaleDateString()} · the floating tutor knows
                you&apos;re on this lesson — ask anything.
              </span>
            </footer>
          </article>

          {/* Sidebar */}
          {lesson.content.flashcards && lesson.content.flashcards.length > 0 && (
            <div className="lg:sticky lg:top-4 lg:self-start">
              <Flashcards
                cards={lesson.content.flashcards}
                topic={lesson.topic}
                lessonSlug={lesson.slug}
                isAuthenticated={isAuthenticated}
              />
            </div>
          )}
        </div>
      </div>
    </main>
  )
}

/* -------------------- subcomponents -------------------- */

/**
 * Splits markdown on `[[diagram:<id>]]` markers and renders each segment.
 * Markers between paragraphs become full-width diagram blocks; we don't
 * try to inline SVGs into running text.
 */
function MarkdownWithDiagrams({
  content,
  diagramsById,
}: {
  content: string
  diagramsById: Map<string, LessonDiagram>
}) {
  const parts = content.split(/(\[\[diagram:[a-zA-Z0-9_-]+\]\])/)
  const blocks: ReactNode[] = []
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]
    const m = part.match(/^\[\[diagram:([a-zA-Z0-9_-]+)\]\]$/)
    if (m) {
      const diagram = diagramsById.get(m[1])
      if (diagram) blocks.push(<Diagram key={`d-${i}`} diagram={diagram} />)
    } else if (part.trim()) {
      blocks.push(
        <div
          key={`md-${i}`}
          className="prose dark:prose-invert max-w-none prose-headings:text-foreground prose-strong:text-brand-ink dark:prose-strong:text-brand-ink prose-code:rounded prose-code:bg-slate-100 dark:prose-code:bg-card prose-code:px-1.5 prose-code:py-0.5 prose-code:text-brand-ink dark:prose-code:text-brand-ink prose-code:before:content-none prose-code:after:content-none"
        >
          <MarkdownRenderer>{part}</MarkdownRenderer>
        </div>,
      )
    }
  }
  return <>{blocks}</>
}

/* WorkedExampleCard moved to src/components/lesson/WorkedExampleCard.tsx */

/* -------------------- helpers -------------------- */

function stripMd(s: string): string {
  return stripMarkdown(s, { lesson: true })
}
